import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, Box, Typography,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert, Stepper, Step, StepLabel, Switch, FormControlLabel
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';

interface EmailTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: any;
  onSave: (templateData: any) => void;
}

const EmailTemplateDialog: React.FC<EmailTemplateDialogProps> = ({ open, onClose, template, onSave }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    subject: template?.subject || '',
    content: template?.content || '',
    status: template?.status || 'Active',
    variables: template?.variables || []
  });

  const [newVariable, setNewVariable] = useState('');

  const handleAddVariable = () => {
    if (newVariable && !formData.variables.includes(newVariable)) {
      setFormData({
        ...formData,
        variables: [...formData.variables, newVariable]
      });
      setNewVariable('');
    }
  };

  const handleRemoveVariable = (variable: string) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter((v: string) => v !== variable)
    });
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle style={{ backgroundColor: '#0927eb', color: 'white' }}>
        {template ? 'Edit Email Template' : 'Create Email Template'}
      </DialogTitle>
      <DialogContent style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '20px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Subject"
              value={formData.subject}
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={8}
              label="Email Content"
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              margin="normal"
              placeholder="Enter your email template content here. Use {{variable}} for dynamic content."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '10px' }}>
              Template Variables
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TextField
                label="Add Variable"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                style={{ marginRight: '10px', flex: 1 }}
                placeholder="e.g., userName, companyName"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddVariable}
                style={{ backgroundColor: '#0927eb' }}
              >
                Add
              </Button>
            </Box>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.variables.map((variable: string) => (
                <Chip
                  key={variable}
                  label={`{{${variable}}}`}
                  onDelete={() => handleRemoveVariable(variable)}
                  style={{ backgroundColor: '#0927eb', color: 'white' }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '20px' }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} style={{ backgroundColor: '#0927eb' }}>
          Save Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateDialog;
